import { produce } from "immer";
import { PulseState, PulseItem } from "./types";
import {
  PulseAction,
  REQUEST_PULSE_SUCCESS,
  SET_PLAYBACK_DURATION,
  REMOVE_PLAYBACK_DURATION,
  FINISH_PULSE_CONTENT,
  END_PULSE_CONTENT,
} from "./actions";
import { DEFAULT_BOB_MAX_DURATION_MS } from "../../constants";

const initialState: PulseState = {
  byId: {},
};

/**
 * Helper function to clean up pulse items with the same zoneId and type.
 * Uses for...in loop for better performance (avoids creating intermediate arrays).
 *
 * @param byId - The draft.byId object from Immer
 * @param zoneId - The zoneId to match
 * @param type - The type to match ("initialize" | "update" | "finished" | "ended")
 * @param excludeKey - Optional key to exclude from deletion (e.g., the current item being updated)
 */
function cleanupPulseItemsByZoneAndType(
  byId: { [key: string]: PulseItem },
  zoneId: string,
  type: PulseItem["type"],
  excludeKey?: string
): void {
  for (const key in byId) {
    const existingItem = byId[key];
    if (
      (!excludeKey || key !== excludeKey) &&
      existingItem &&
      existingItem.zoneId === zoneId &&
      existingItem.type === type
    ) {
      delete byId[key];
    }
  }
}

export function pulseReducer(
  state = initialState,
  action: PulseAction
): PulseState {
  return produce(state, (draft) => {
    switch (action.type) {
      case REQUEST_PULSE_SUCCESS: {
        const { pulseItem } = action.payload;
        // Clean up all existing "initialize" items with the same zoneId before adding the new one
        cleanupPulseItemsByZoneAndType(
          draft.byId,
          pulseItem.zoneId,
          "initialize"
        );
        // Always add/update the pulse item (allows re-adding after deletion)
        draft.byId[pulseItem.pulseItemKey] = {
          id: pulseItem.id,
          type: pulseItem.type,
          zoneId: pulseItem.zoneId,
          pulseItemKey: pulseItem.pulseItemKey,
          viewerUrl: pulseItem.viewerUrl,
          config: pulseItem.config,
          playbackWillFinishInMs: DEFAULT_BOB_MAX_DURATION_MS,
        };
        break;
      }

      case SET_PLAYBACK_DURATION: {
        const { pulseItemKey, durationMs } = action.payload;
        const currentItem = draft.byId[pulseItemKey];
        if (!currentItem) {
          break;
        }
        // No cleanup needed - pulseItemKey is unique, just update the item
        draft.byId[pulseItemKey] = {
          ...draft.byId[pulseItemKey],
          type: "update",
          playbackWillFinishInMs: durationMs,
        };
        break;
      }

      case REMOVE_PLAYBACK_DURATION: {
        const { pulseItemKey } = action.payload;
        const pulseItem = draft.byId[pulseItemKey];
        if (pulseItem) {
          delete draft.byId[pulseItemKey];
        }
        break;
      }

      case FINISH_PULSE_CONTENT: {
        const { pulseItemKey } = action.payload;
        const pulseItem = draft.byId[pulseItemKey];
        if (pulseItem) {
          // Update the type to "finished" to signal completion
          draft.byId[pulseItemKey] = {
            ...draft.byId[pulseItemKey],
            type: "finished",
          };
        }
        break;
      }
      case END_PULSE_CONTENT: {
        const { pulseItemKey } = action.payload;
        const currentItem = draft.byId[pulseItemKey];
        if (!currentItem) {
          break;
        }
        // Clean up all existing "ended" items with the same zoneId (excluding the current one)
        cleanupPulseItemsByZoneAndType(
          draft.byId,
          currentItem.zoneId,
          "ended",
          pulseItemKey
        );
        // Update the current item to "ended" type
        draft.byId[pulseItemKey] = {
          ...draft.byId[pulseItemKey],
          type: "ended",
        };
        break;
      }
      default:
        break;
    }
  });
}
